import { useStepperContext } from '@folio/components';
import { evaluateCondition } from '@folio/components/forms/form-builder/conditionalVisibilityUtils';
import { fieldsOnDynamicStep, useDynamicSteps } from '@folio/hooks';
import type { ConfigCustomFieldDto } from 'korint-sdk';
import type {
  ConditionalVisibility,
  FieldShortcut,
  FormSectionsWithShortcuts,
} from 'korint-sdk/react';
import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useRoutes,
  useSearchParams,
} from 'react-router-dom';
import type { ZodTypeAny } from 'zod';
import { AssetFunnelModule } from '../AssetFunnelModule/AssetFunnelModule';
import { CustomerFunnelModule } from '../CustomerFunnelModule/CustomerFunnelModule';
import { PolicyFunnelModule } from '../PolicyFunnelModule/PolicyFunnelModule';

type Stepper = ReturnType<typeof useStepperContext>;

function toFieldShortcut(
  { key, display, validationRules }: ConfigCustomFieldDto,
  blockingMessage: string,
): FieldShortcut {
  const enumValues = validationRules?.find(({ option }) => option === 'enum')?.value as
    | string[]
    | undefined;
  const optionLabels = display?.optionLabels as Record<string, string> | undefined;
  const showWhen = display?.showWhen as ConditionalVisibility[] | undefined;
  const blockingWhen = display?.blockingWhen as ConditionalVisibility[] | undefined;
  const isBlocking = (value: unknown) =>
    (blockingWhen ?? []).every(
      condition => condition.fieldName === key && evaluateCondition(condition, value),
    );

  return {
    name: key,
    label: display?.label,
    ...(enumValues &&
      optionLabels && {
        options: enumValues.map(value => ({ value, label: optionLabels[value] ?? value })),
      }),
    ...(showWhen?.length && { showWhen }),
    ...(blockingWhen?.length && {
      blockingWhen,
      blockingErrorMessage: blockingMessage,
      additionalValidationRules: (schema: ZodTypeAny) =>
        schema.refine(value => !isBlocking(value), { message: blockingMessage }),
    }),
  };
}

function WithStepper({ stepper, children }: { stepper: Stepper; children: ReactNode }) {
  const outletContext = useOutletContext<Record<string, unknown>>();

  return useRoutes([
    {
      element: <Outlet context={{ ...outletContext, stepper }} />,
      children: [{ index: true, element: children }],
    },
  ]);
}

export function DynamicStepsModule() {
  const { branchId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const stepper = useStepperContext();
  const { t } = useTranslation();
  const { config, steps } = useDynamicSteps();

  const requestedIndex = steps.findIndex(({ key }) => key === searchParams.get('step'));
  const isBackNavigation = location.state?.navigationDirection === 'back';
  const index = requestedIndex === -1 ? (isBackNavigation ? steps.length - 1 : 0) : requestedIndex;
  const current = steps[index];

  useEffect(() => {
    if (requestedIndex === -1 && current) {
      navigate({ search: `?step=${current.key}` }, { replace: true, state: location.state });
    }
  }, [requestedIndex, current, navigate, location.state]);

  if (!config || !current) {
    return null;
  }

  const goTo = (key: string, replaceParams?: Record<string, string>) =>
    navigate({
      pathname:
        branchId && replaceParams?.branchId
          ? location.pathname.replace(branchId, replaceParams.branchId)
          : location.pathname,
      search: `?step=${key}`,
    });

  const dynamicStepper: Stepper = {
    ...stepper,
    isFirstStep: stepper.isFirstStep && index === 0,
    isLastStep: stepper.isLastStep && index === steps.length - 1,
    next: replaceParams =>
      index < steps.length - 1
        ? goTo(steps[index + 1].key, replaceParams)
        : stepper.next(replaceParams),
    previous: replaceParams =>
      index > 0 ? goTo(steps[index - 1].key, replaceParams) : stepper.previous(replaceParams),
  };

  const toSections = (fields: ConfigCustomFieldDto[]): FormSectionsWithShortcuts => [
    {
      title: current.label,
      fields: fields.map(field => toFieldShortcut(field, t('funnelSteps.dynamic.blockingMessage'))),
    },
  ];
  const customerFields = fieldsOnDynamicStep(config.customerFields, current.key);
  const assetFields = fieldsOnDynamicStep(config.assetFields, current.key);
  const policyFields = fieldsOnDynamicStep(config.policyFields, current.key);

  return (
    <WithStepper key={current.key} stepper={dynamicStepper}>
      {customerFields.length > 0 ? (
        <CustomerFunnelModule sections={toSections(customerFields)} path={current.key} />
      ) : assetFields.length > 0 ? (
        <AssetFunnelModule
          sections={toSections(assetFields)}
          path={current.key}
          minAssets={1}
          maxAssets={1}
        />
      ) : (
        <PolicyFunnelModule sections={toSections(policyFields)} path={current.key} />
      )}
    </WithStepper>
  );
}
